import React, { useState, useEffect } from 'react';
import Select from "react-select";
import { inventoryApiService } from '../../api';

// ─────────────────────────────────────────────
// DESIGN SYSTEM – mirrors Dashboard.js exactly
// ─────────────────────────────────────────────
const COLORS = {
  aquaBlue: '#11a8a4',
  grassGreen: '#7ed957',
  darkGrey: '#3c3b3f',
  vividPurple: '#9440dd',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  lightText: '#9ca3af',
};

// ─────────────────────────────────────────────
// SMALL REUSABLE STYLE HELPERS
// ─────────────────────────────────────────────
const cardStyle = {
  backgroundColor: COLORS.white,
  borderRadius: '8px',
  border: `1px solid ${COLORS.mediumGrey}`,
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
  padding: '24px',
  marginBottom: '24px',
};

const sectionTitleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: COLORS.darkText,
  margin: '0 0 16px 0',
  paddingBottom: '10px',
  borderBottom: `2px solid ${COLORS.mediumGrey}`,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: COLORS.darkText,
  marginBottom: '6px',
};

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  border: `1px solid ${COLORS.mediumGrey}`,
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  backgroundColor: COLORS.white,
  color: COLORS.darkText,
  boxSizing: 'border-box',
};

const primaryBtnStyle = (disabled = false) => ({
  backgroundColor: disabled ? COLORS.mediumGrey : COLORS.aquaBlue,
  color: COLORS.white,
  border: 'none',
  borderRadius: '6px',
  padding: '10px 20px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontWeight: '500',
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  opacity: disabled ? 0.7 : 1,
  transition: 'background-color 0.2s',
});

const outlineBtnStyle = {
  backgroundColor: COLORS.white,
  color: COLORS.mediumText,
  border: `1px solid ${COLORS.mediumGrey}`,
  borderRadius: '6px',
  padding: '9px 18px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '14px',
  transition: 'background-color 0.2s',
};

const dangerIconBtnStyle = {
  backgroundColor: '#fee2e2',
  color: '#dc2626',
  border: 'none',
  borderRadius: '6px',
  padding: '7px 10px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600',
  flexShrink: 0,
};

const addRowBtnStyle = {
  backgroundColor: `${COLORS.aquaBlue}15`,
  color: COLORS.aquaBlue,
  border: `1px dashed ${COLORS.aquaBlue}`,
  borderRadius: '6px',
  padding: '8px 16px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '14px',
  marginTop: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const spinnerStyle = {
  width: '16px',
  height: '16px',
  border: '2px solid transparent',
  borderTop: '2px solid white',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  flexShrink: 0,
};

// ─────────────────────────────────────────────
// MONTHS & YEARS
// ─────────────────────────────────────────────
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const buildYears = () => {
  const current = new Date().getFullYear();
  return [current - 1, current, current + 1];
};

// ─────────────────────────────────────────────
// ITEM ROW COMPONENT
// ─────────────────────────────────────────────
const ItemRow = ({ row, index, allItems, usedIds, onChange, onRemove, category }) => {
  const available = allItems.filter(
    (item) => item.category === category && (!usedIds.includes(item.id) || item.id === row.inventory_item)
  );

  const qtyError = row.inventory_item && row.quantity_assigned !== '' && Number(row.quantity_assigned) <= 0;
  const threshError =
    row.inventory_item &&
    row.low_stock_threshold !== '' &&
    row.quantity_assigned !== '' &&
    Number(row.low_stock_threshold) > Number(row.quantity_assigned);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 110px 110px 36px',
        gap: '8px',
        alignItems: 'start',
        backgroundColor: COLORS.lightGrey,
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '8px',
      }}
    >
      {/* Item dropdown */}
      <div>
        {index === 0 && (
          <label style={{ ...labelStyle, marginBottom: '4px' }}>Inventory Item</label>
        )}
        <select
          value={row.inventory_item}
          onChange={(e) => onChange(index, 'inventory_item', parseInt(e.target.value) || '')}
          style={{
            ...inputStyle,
            borderColor: !row.inventory_item ? '#f59e0b' : COLORS.mediumGrey,
          }}
        >
          <option value="">Select item…</option>
          {available.map((item) => (
            <option key={item.id} value={item.id}>
              {item.item_name} ({item.item_code})
            </option>
          ))}
        </select>
      </div>

      {/* Quantity */}
      <div>
        {index === 0 && (
          <label style={{ ...labelStyle, marginBottom: '4px' }}>Quantity</label>
        )}
        <input
          type="number"
          min="1"
          placeholder="Qty"
          value={row.quantity_assigned}
          onChange={(e) => onChange(index, 'quantity_assigned', e.target.value)}
          style={{
            ...inputStyle,
            borderColor: qtyError ? '#ef4444' : COLORS.mediumGrey,
          }}
        />
        {qtyError && (
          <p style={{ color: '#ef4444', fontSize: '11px', margin: '2px 0 0' }}>Must be &gt; 0</p>
        )}
      </div>

      {/* Threshold */}
      <div>
        {index === 0 && (
          <label style={{ ...labelStyle, marginBottom: '4px' }}>Low Stock Alert</label>
        )}
        <input
          type="number"
          min="0"
          placeholder="Threshold"
          value={row.low_stock_threshold}
          onChange={(e) => onChange(index, 'low_stock_threshold', e.target.value)}
          style={{
            ...inputStyle,
            borderColor: threshError ? '#ef4444' : COLORS.mediumGrey,
          }}
        />
        {threshError && (
          <p style={{ color: '#ef4444', fontSize: '11px', margin: '2px 0 0' }}>≤ quantity</p>
        )}
      </div>

      {/* Remove */}
      <div style={{ paddingTop: index === 0 ? '22px' : '0' }}>
        <button
          onClick={() => onRemove(index)}
          style={dangerIconBtnStyle}
          title="Remove row"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const MonthlyInventoryAssignment = () => {
  const today = new Date();

  // ── data ──
  const [technicians, setTechnicians] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── form ──
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  // ── item rows ──
  const emptyRow = () => ({ inventory_item: '', quantity_assigned: '', low_stock_threshold: '' });
  const [consumables, setConsumables] = useState([emptyRow()]);
  const [capitalItems, setCapitalItems] = useState([emptyRow()]);

  // ── submission ──
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // ─── fetch initial data ───
  useEffect(() => {
    const init = async () => {
      try {
        const [techs, items] = await Promise.all([
          inventoryApiService.getTechnicians(),
          inventoryApiService.getInventoryItems(),
        ]);
        setTechnicians(techs);
        setInventoryItems(items);
      } catch (err) {
        console.error('Error loading data:', err);
        alert('Failed to load initial data. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ─── row helpers ───
  const handleRowChange = (setter) => (index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };
      if (field === 'quantity_assigned') {
        row.quantity_remaining = value; // auto-fill
      }
      updated[index] = row;
      return updated;
    });
  };

  const addRow = (setter) => () => setter((prev) => [...prev, emptyRow()]);

  const removeRow = (setter) => (index) =>
    setter((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  // ─── validation ───
  const validate = () => {
    if (!selectedTechnician) {
      alert('Please select a technician.');
      return false;
    }

    const allConsumable = consumables.filter((r) => r.inventory_item);
    const allCapital = capitalItems.filter((r) => r.inventory_item);

    if (allConsumable.length === 0 && allCapital.length === 0) {
      alert('Add at least one inventory item (consumable or capital).');
      return false;
    }

    for (const r of [...allConsumable, ...allCapital]) {
      if (!r.quantity_assigned || Number(r.quantity_assigned) <= 0) {
        alert('All added items must have a quantity greater than 0.');
        return false;
      }
      if (r.low_stock_threshold !== '' && Number(r.low_stock_threshold) > Number(r.quantity_assigned)) {
        alert('Low stock threshold cannot exceed quantity assigned.');
        return false;
      }
    }

    return true;
  };


  const technicianOptions = technicians.map((tech) => ({
    value: tech.id,
    label:
      tech.technician_name ||
      tech.technician_email ||
      tech.name ||
      `Technician #${tech.id}`,
  }));

  // ─── submit ───
  const handleDispatch = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSuccessMsg('');

    try {
      // Step 1 – Create bag
      const bag = await inventoryApiService.createInventoryBag({
        current_technician: parseInt(selectedTechnician),
        month,
        year,
        status: 'ACTIVE',
      });
      const bagId = bag.id;

      // Step 2 – Add consumables
      const consumablePayload = consumables
        .filter((r) => r.inventory_item && Number(r.quantity_assigned) > 0)
        .map((r) => ({
          inventory_item: r.inventory_item,
          quantity_assigned: Number(r.quantity_assigned),
          quantity_remaining: Number(r.quantity_assigned),
          low_stock_threshold: Number(r.low_stock_threshold) || 0,
        }));

      if (consumablePayload.length > 0) {
        await inventoryApiService.addConsumables(bagId, consumablePayload);
      }

      // Step 3 – Add capital items
      const capitalPayload = capitalItems
        .filter((r) => r.inventory_item && Number(r.quantity_assigned) > 0)
        .map((r) => ({
          inventory_item: r.inventory_item,
          quantity_assigned: Number(r.quantity_assigned),
          quantity_remaining: Number(r.quantity_assigned),
          low_stock_threshold: Number(r.low_stock_threshold) || 0,
        }));

      if (capitalPayload.length > 0) {
        await inventoryApiService.addCapitalItems(bagId, capitalPayload);
      }

      // Step 4 – Dispatch
      await inventoryApiService.dispatchInventory(bagId, parseInt(selectedTechnician));

      setSuccessMsg(
        `✅ Inventory bag dispatched successfully! (Bag ID: ${bagId})`
      );
      handleReset();
    } catch (err) {
      console.error('Error dispatching inventory:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      alert(`Failed to dispatch inventory: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedTechnician('');
    setMonth(today.getMonth() + 1);
    setYear(today.getFullYear());
    setConsumables([emptyRow()]);
    setCapitalItems([emptyRow()]);
  };

  const usedConsumableIds = consumables.map((r) => r.inventory_item).filter(Boolean);
  const usedCapitalIds = capitalItems.map((r) => r.inventory_item).filter(Boolean);

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px',
          gap: '12px',
        }}
      >
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div
          style={{
            border: '4px solid #e5e7eb',
            borderTop: `4px solid ${COLORS.aquaBlue}`,
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ color: COLORS.mediumText, fontSize: '15px', margin: 0 }}>
          Loading inventory data…
        </p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", padding: '0' }}>
      {/* Keyframe for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: COLORS.darkText }}>
          Monthly Inventory Assignment
        </h2>
        <p style={{ margin: 0, color: COLORS.mediumText, fontSize: '14px' }}>
          Assign monthly inventory bags to technicians
        </p>
      </div>

      {/* ── Success banner ── */}
      {successMsg && (
        <div
          style={{
            backgroundColor: '#f0fdf4',
            border: `1px solid ${COLORS.grassGreen}`,
            borderRadius: '8px',
            padding: '14px 18px',
            marginBottom: '20px',
            color: '#166534',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {successMsg}
          <button
            onClick={() => setSuccessMsg('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '16px' }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── TOP FORM SECTION ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          <span
            style={{
              width: '4px',
              height: '18px',
              backgroundColor: COLORS.aquaBlue,
              borderRadius: '2px',
              display: 'inline-block',
            }}
          />
          Assignment Details
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
          }}
        >
          {/* Technician dropdown */}
<div>
  <label style={labelStyle}>Technician *</label>

  <Select
    options={technicianOptions}
    value={
      technicianOptions.find(
        (option) => option.value === selectedTechnician
      ) || null
    }
    onChange={(selectedOption) =>
      setSelectedTechnician(selectedOption?.value || "")
    }
    placeholder="Search and select technician..."
    isSearchable
    styles={{
      control: (provided) => ({
        ...provided,
        minHeight: "42px",
        borderColor: !selectedTechnician
          ? "#f59e0b"
          : COLORS.mediumGrey,
      }),
    }}
  />
</div>

          {/* Month */}
          <div>
            <label style={labelStyle}>Month *</label>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = COLORS.aquaBlue)}
              onBlur={(e) => (e.target.style.borderColor = COLORS.mediumGrey)}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label style={labelStyle}>Year *</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = COLORS.aquaBlue)}
              onBlur={(e) => (e.target.style.borderColor = COLORS.mediumGrey)}
            >
              {buildYears().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── CONSUMABLES SECTION ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          <span
            style={{
              width: '4px',
              height: '18px',
              backgroundColor: COLORS.grassGreen,
              borderRadius: '2px',
              display: 'inline-block',
            }}
          />
          Consumable Items
          <span
            style={{
              marginLeft: 'auto',
              backgroundColor: `${COLORS.grassGreen}20`,
              color: COLORS.grassGreen,
              fontSize: '12px',
              fontWeight: '600',
              padding: '2px 8px',
              borderRadius: '12px',
            }}
          >
            {consumables.filter((r) => r.inventory_item).length} added
          </span>
        </h3>

        {inventoryItems.filter((i) => i.category === 'CONSUMABLE').length === 0 ? (
          <p style={{ color: COLORS.mediumText, fontSize: '14px', textAlign: 'center', padding: '16px 0' }}>
            No consumable items found.
          </p>
        ) : (
          <>
            {consumables.map((row, idx) => (
              <ItemRow
                key={idx}
                row={row}
                index={idx}
                allItems={inventoryItems}
                usedIds={usedConsumableIds}
                onChange={handleRowChange(setConsumables)}
                onRemove={removeRow(setConsumables)}
                category="CONSUMABLE"
              />
            ))}

            <button style={addRowBtnStyle} onClick={addRow(setConsumables)}>
              <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span> Add Consumable Row
            </button>
          </>
        )}
      </div>

      {/* ── CAPITAL ITEMS SECTION ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          <span
            style={{
              width: '4px',
              height: '18px',
              backgroundColor: COLORS.vividPurple,
              borderRadius: '2px',
              display: 'inline-block',
            }}
          />
          Capital Items
          <span
            style={{
              marginLeft: 'auto',
              backgroundColor: `${COLORS.vividPurple}18`,
              color: COLORS.vividPurple,
              fontSize: '12px',
              fontWeight: '600',
              padding: '2px 8px',
              borderRadius: '12px',
            }}
          >
            {capitalItems.filter((r) => r.inventory_item).length} added
          </span>
        </h3>

        {inventoryItems.filter((i) => i.category === 'CAPITAL').length === 0 ? (
          <p style={{ color: COLORS.mediumText, fontSize: '14px', textAlign: 'center', padding: '16px 0' }}>
            No capital items found.
          </p>
        ) : (
          <>
            {capitalItems.map((row, idx) => (
              <ItemRow
                key={idx}
                row={row}
                index={idx}
                allItems={inventoryItems}
                usedIds={usedCapitalIds}
                onChange={handleRowChange(setCapitalItems)}
                onRemove={removeRow(setCapitalItems)}
                category="CAPITAL"
              />
            ))}

            <button style={addRowBtnStyle} onClick={addRow(setCapitalItems)}>
              <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span> Add Capital Row
            </button>
          </>
        )}
      </div>

      {/* ── STICKY FOOTER ACTIONS ── */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.mediumGrey}`,
          borderRadius: '8px',
          padding: '14px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
          zIndex: 50,
        }}
      >
        <button
          onClick={handleReset}
          disabled={submitting}
          style={{ ...outlineBtnStyle, opacity: submitting ? 0.6 : 1 }}
          onMouseEnter={(e) => {
            if (!submitting) e.currentTarget.style.backgroundColor = COLORS.lightGrey;
          }}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.white)}
        >
          Reset
        </button>

        <button
          onClick={handleDispatch}
          disabled={submitting}
          style={primaryBtnStyle(submitting)}
          onMouseEnter={(e) => {
            if (!submitting) e.currentTarget.style.backgroundColor = '#0d8f8b';
          }}
          onMouseLeave={(e) => {
            if (!submitting) e.currentTarget.style.backgroundColor = COLORS.aquaBlue;
          }}
        >
          {submitting && <div style={spinnerStyle} />}
          {submitting ? 'Dispatching…' : '🚀 Dispatch Inventory'}
        </button>
      </div>
    </div>
  );
};

export default MonthlyInventoryAssignment;